import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { getAMIsByHash } from '../services/api';
import type { HashOccurrence } from '../types/api';
import { 
  Container, 
  Typography, 
  CircularProgress, 
  Alert, 
  Paper, 
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import Navbar from '../components/Navbar';

const HashResults = () => {
  const { hashId } = useParams<{ hashId: string }>();
  const navigate = useNavigate();
  const [results, setResults] = useState<HashOccurrence[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (hashId) {
      setLoading(true);
      setError(null);
      getAMIsByHash(hashId)
        .then(res => {
          if (res.error) {
            setError(res.error);
            setResults([]);
          } else {
            setResults(Array.isArray(res.data) ? res.data : []);
          }
          setLoading(false);
        })
        .catch(() => {
          setError('Failed to fetch hash lookup results');
          setLoading(false);
        });
    }
  }, [hashId]);

  return (
    <Container maxWidth="lg" sx={{ px: 2, pb: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%', mt: 4, mb: 4 }}>
        <Navbar />
      </Box>
      
      <Typography variant="h4" gutterBottom align="center">
        Hash Lookup Results
      </Typography>
      <Typography color="text.secondary" paragraph align="center">
        Files containing hash: {hashId}
      </Typography>
      <Typography sx={{ mb: 4, textAlign: 'center' }}>
        <RouterLink to="/">← Back to Home</RouterLink>
      </Typography>

      {loading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mt: 4 }}>
          {error}
        </Alert>
      )}

      {!loading && !error && results.length === 0 && (
        <Alert severity="info" sx={{ mt: 4 }}>
          No AMIs found containing this hash
        </Alert>
      )}

      {/* Hash Lookup Results */}
      {results.length > 0 && (
        <Box mt={4}>
          <Typography variant="h6" gutterBottom>
            {(() => {
              const uniqueAccounts = new Set(results.map(r => r.accountId)).size;
              const uniqueAmis = new Set(results.map(r => r.amiId)).size;
              return `Hash Found in ${results.length} files [${uniqueAccounts} unique accounts, ${uniqueAmis} unique AMIs]`;
            })()}
          </Typography>
          
          {/* Desktop Table View */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Account ID</TableCell>
                    <TableCell>AMI ID</TableCell>
                    <TableCell>File Path</TableCell>
                    <TableCell>Modified Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((result, index) => (
                    <TableRow
                      key={`${result.amiId}-${index}`}
                      hover
                    >
                      <TableCell 
                        sx={{ 
                          cursor: 'pointer', 
                          color: 'primary.main', 
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          minWidth: '100px',
                          '&:hover': { textDecoration: 'underline' } 
                        }}
                        onClick={() => navigate(`/account/${result.accountId}`)}
                      >
                        {result.accountId}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          cursor: 'pointer', 
                          color: 'primary.main', 
                          fontFamily: 'monospace',
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap',
                          minWidth: '140px',
                          '&:hover': { textDecoration: 'underline' } 
                        }}
                        onClick={() => navigate(`/image/${result.amiId}`)}
                      >
                        {result.amiId}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          cursor: 'pointer', 
                          color: 'primary.main',
                          fontFamily: 'monospace', 
                          fontSize: '0.75rem', 
                          wordBreak: 'break-all',
                          '&:hover': { textDecoration: 'underline' }
                        }}
                        onClick={() => {
                          // Navigate to search page and perform exact filepath search
                          navigate(`/search`);
                          // Note: We can't directly trigger the search from here since we'd need 
                          // access to the search state. User will need to manually enter the path.
                        }}
                      >
                        {result.relativePath}
                      </TableCell>
                      <TableCell 
                        sx={{ 
                          fontSize: '0.75rem',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {new Date(result.modifiedTimestamp).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {results.map((result, index) => (
              <Paper 
                key={`${result.amiId}-${index}`}
                sx={{ p: 2, mb: 2 }}
              >
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Account</Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main', 
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/account/${result.accountId}`)}
                    >
                      {result.accountId}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">AMI ID</Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main', 
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => navigate(`/image/${result.amiId}`)}
                    >
                      {result.amiId}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="caption" color="text.secondary">Modified</Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ fontSize: '0.75rem' }}
                    >
                      {new Date(result.modifiedTimestamp).toLocaleString()}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                      File Path
                    </Typography>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        cursor: 'pointer', 
                        color: 'primary.main',
                        fontFamily: 'monospace',
                        fontSize: '0.75rem',
                        wordBreak: 'break-all',
                        '&:hover': { textDecoration: 'underline' }
                      }}
                      onClick={() => {
                        // Navigate to search page for exact filepath search
                        navigate(`/search`);
                      }}
                    >
                      {result.relativePath}
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default HashResults; 